import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { getCurrentUser } from '../lib/auth';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { FaCrown, FaCheck, FaLock, FaWallet, FaPlus } from 'react-icons/fa';

interface SubscriptionModalProps {
  onClose: () => void;
  onSubscribe: () => void;
}

const SubscriptionModal: React.FC<SubscriptionModalProps> = ({ onClose, onSubscribe }) => {
  const [loading, setLoading] = useState(false);
  const [userBalance, setUserBalance] = useState(0);
  const user = getCurrentUser();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) {
      loadUserBalance();
    }
  }, [user]);

  const loadUserBalance = async () => {
    try {
      const { data, error } = await supabase
        .from('ponousers')
        .select('wallet_balance')
        .eq('id', user?.id)
        .single();

      if (error) throw error;
      setUserBalance(data.wallet_balance || 0);
    } catch (error) {
      console.error('Error loading balance:', error);
    }
  };

  const handleStartTrial = async () => {
    if (!user) {
      toast.error('Please log in to start your trial');
      return;
    }

    try {
      setLoading(true);

      // Check if user has had a trial before
      const { data: existingTrial, error: trialError } = await supabase
        .from('ponocouponusage')
        .select('*')
        .eq('user_id', user.id)
        .gte('valid_until', new Date().toISOString())
        .maybeSingle();

      if (existingTrial) {
        toast.error('You have already used your free trial');
        return;
      }

      // Create trial access
      const trialEnd = new Date();
      trialEnd.setDate(trialEnd.getDate() + 3); // 3 days trial

      const { error: usageError } = await supabase
        .from('ponocouponusage')
        .insert({
          user_id: user.id,
          valid_until: trialEnd.toISOString()
        });

      if (usageError) throw usageError;

      toast.success('Free trial activated! Enjoy 3 days of full access');
      onSubscribe();
    } catch (error: any) {
      console.error('Trial error:', error);
      toast.error(error.message || 'Failed to start trial');
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async () => {
    if (!user) {
      toast.error('Please log in to subscribe');
      return;
    }

    try {
      setLoading(true);

      // Check balance
      if (userBalance < 15) {
        toast.error('Insufficient balance. Please add funds to your wallet.');
        return;
      }

      // Calculate subscription end date
      const subscriptionEnd = new Date();
      subscriptionEnd.setMonth(subscriptionEnd.getMonth() + 1);

      // Check existing subscription
      const { data: existingUsage, error: usageError } = await supabase
        .from('ponocouponusage')
        .select('valid_until')
        .eq('user_id', user.id)
        .gte('valid_until', new Date().toISOString())
        .maybeSingle();

      if (usageError && usageError.code !== 'PGRST116') throw usageError;

      // Calculate new validity period
      const validUntil = existingUsage
        ? new Date(existingUsage.valid_until)
        : new Date();
      validUntil.setDate(validUntil.getDate() + 15); // Add 15 days

      // Create or update subscription record
      const { error: subscriptionError } = await supabase
        .from('ponocouponusage')
        .upsert({
          user_id: user.id,
          valid_until: validUntil.toISOString()
        });

      if (subscriptionError) throw subscriptionError;

      // Deduct subscription fee from wallet
      const { error: walletError } = await supabase
        .from('ponousers')
        .update({ wallet_balance: userBalance - 15 })
        .eq('id', user.id);

      if (walletError) throw walletError;

      // Create transaction record
      const { error: transactionError } = await supabase
        .from('ponotransactions')
        .insert({
          user_id: user.id,
          amount: 15,
          type: 'withdraw',
          status: 'completed',
          description: 'Monthly subscription payment'
        });

      if (transactionError) throw transactionError;

      // Update local user data
      const updatedUser = {
        ...user,
        wallet_balance: userBalance - 15
      };
      localStorage.setItem('user', JSON.stringify(updatedUser));

      toast.success('Subscription activated successfully!');
      onSubscribe();
    } catch (error: any) {
      console.error('Subscription error:', error);
      toast.error(error.message || 'Failed to process subscription');
    } finally {
      setLoading(false);
    }
  };

  const handleAddFunds = () => {
    onClose();
    navigate('/wallet');
  };

  const handleMaybeLater = () => {
    onClose();
    navigate('/dashboard');
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-2 sm:p-4">
      <div className="bg-gray-900 rounded-xl w-full max-w-sm mx-auto">
        <div className="p-4 sm:p-6 text-center">
          <FaCrown className="w-12 h-12 sm:w-16 sm:h-16 text-yellow-500 mx-auto mb-3 sm:mb-4" />
          <h2 className="text-xl sm:text-2xl font-bold text-white mb-2">Premium Access</h2>
          <p className="text-sm sm:text-base text-gray-400 mb-4 sm:mb-6">
            Get unlimited access to all videos
          </p>

          <div className="bg-gray-800 rounded-lg p-4 sm:p-6 mb-4 sm:mb-6">
            <div className="flex justify-between items-center mb-3 sm:mb-4">
              <span className="text-sm sm:text-base text-white">Monthly</span>
              <span className="text-xl sm:text-2xl font-bold text-yellow-500">$15</span>
            </div>
            <div className="space-y-2 text-left mb-4 sm:mb-6">
              <div className="flex items-center text-sm sm:text-base text-gray-300">
                <FaCheck className="w-3 h-3 sm:w-4 sm:h-4 text-green-500 mr-2" />
                <span>Unlimited videos</span>
              </div>
              <div className="flex items-center text-sm sm:text-base text-gray-300">
                <FaCheck className="w-3 h-3 sm:w-4 sm:h-4 text-green-500 mr-2" />
                <span>HD quality</span>
              </div>
              <div className="flex items-center text-sm sm:text-base text-gray-300">
                <FaCheck className="w-3 h-3 sm:w-4 sm:h-4 text-green-500 mr-2" />
                <span>No ads</span>
              </div>
            </div>

            <div className="flex items-center justify-between text-xs sm:text-sm text-gray-400 mb-3">
              <div className="flex items-center">
                <FaWallet className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                <span>Balance:</span>
              </div>
              <span>${userBalance.toFixed(2)}</span>
            </div>

            <button
              onClick={handleStartTrial}
              disabled={loading}
              className="w-full bg-red-600 text-white py-2 sm:py-3 rounded-lg text-sm sm:text-base font-medium hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors mb-2 sm:mb-4"
            >
              {loading ? 'Processing...' : 'Start 3-Day Trial'}
            </button>

            {userBalance < 15 ? (
              <button
                onClick={handleAddFunds}
                className="w-full bg-green-600 text-white py-2 sm:py-3 rounded-lg text-sm sm:text-base font-medium hover:bg-green-700 transition-colors mb-2 sm:mb-4 flex items-center justify-center"
              >
                <FaPlus className="w-3 h-3 sm:w-4 sm:h-4 mr-2" />
                Add Funds
              </button>
            ) : (
              <button
                onClick={handleSubscribe}
                disabled={loading}
                className="w-full bg-yellow-500 text-black py-2 sm:py-3 rounded-lg text-sm sm:text-base font-medium hover:bg-yellow-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors mb-2 sm:mb-4"
              >
                {loading ? 'Processing...' : 'Subscribe'}
              </button>
            )}

            {userBalance < 15 && (
              <p className="text-red-400 text-xs sm:text-sm">
                Add funds to subscribe
              </p>
            )}
          </div>

          <button
            onClick={handleMaybeLater}
            className="mt-4 sm:mt-6 text-sm sm:text-base text-gray-400 hover:text-white transition-colors"
          >
            Maybe Later
          </button>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionModal;